import EntityMember from "_shared/entity-members/entity-member";
import { withoutFeeScheduleTeamMember } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import { Masked, MaskedTeam } from "work/masked-fields";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/**
 * Who an active proposal's rate may name without adding anybody: the leader, and every member the
 * redlined team holds right now (#5798).
 *
 * A member one side added that the other has not accepted is on the roster, marked proposed: it is
 * on the team the proposal would hire if the counterparty agrees, and the counterparty sees both
 * changes together. A member one side removed is not on it, decided or not, because
 * `resultingEntry` has already dropped them.
 *
 * `vendorTeam` is the proposal's team, which carries the leader; the leader is not redlined here.
 * Without a redline the proposal's team is the whole answer. A team masked to this viewer, in
 * either place, is a masked roster. `clientTeam` says whose people the roster leaves out (#6091).
 */
export function getFeeScheduleTeamRosterFromRedline(
  vendorTeam: VendorTeam | MaskedTeam | null | undefined,
  redline: ProposalRedline | undefined,
  clientTeam?: ClientTeam | MaskedTeam | null,
): FeeScheduleTeamRoster {
  if (!redline || Masked.isMasked(vendorTeam)) return FeeScheduleTeamRoster.fromVendorTeam(vendorTeam, clientTeam);
  if (Masked.isMasked(redline.vendorTeam)) return FeeScheduleTeamRoster.masked();
  const leader = vendorTeam?.leader;
  return new FeeScheduleTeamRoster({
    entries: [
      ...(leader ? [{ member: leader, isLeader: true, isProposed: false }] : []),
      ...redline.vendorTeam.redlines.flatMap((memberRedline) => {
        const member = memberRedline.resultingEntry;
        if (!member) return [];
        return [{ member, isLeader: false, isProposed: memberRedline.isAdded && !memberRedline.isAccepted }];
      }),
    ],
    clientLegalEntityId: FeeScheduleTeamRoster.clientLegalEntityIdOf(clientTeam),
  });
}

/**
 * The same proposal redline, with these people proposed for the vendor team.
 *
 * Adding somebody to an active proposal's team is a redline like any other: the counterparty sees
 * it and may reject it. That is what #5237's "add them to the team" means once a proposal is under
 * negotiation, and it is why this goes through the team redline rather than writing a roster. A
 * template applied to the proposal asks it, and so does a rate added by hand that names somebody
 * the team does not have (#5393).
 *
 * A masked team cannot be redlined, so it comes back unchanged.
 */
export function withStaffingSeatedOnTeamRedline(
  redline: ProposalRedline,
  membersToAdd: readonly EntityMember[],
): ProposalRedline {
  if (membersToAdd.length === 0) return redline;
  if (Masked.isMasked(redline.vendorTeam)) return redline;
  const seated = membersToAdd.reduce(
    (teamRedline, member) => teamRedline.addEntry(member),
    redline.vendorTeam,
  );
  return redline.updateTeamMembersRedline(seated);
}

/**
 * The redline after a member edit, with every rate that names somebody the edit took off the team
 * set back to unassigned.
 *
 * The active proposal's counterpart of what `ProposalBuilder.setVendorTeam` does on a draft (#5561):
 * a rate still naming a person who left the team is what `WorkAgreement.ValidateFeeSchedule` refuses
 * at hire. Each rate goes through `reviseEntryById`, so the counterparty sees a Team Member redline on
 * that row and nothing else.
 *
 * `before` is the redline the edit started from. Only a person who was a member there and is not in
 * `after` counts as leaving; the leader never sits in the member list, so seating a member as leader
 * is not a departure. A masked team, a masked fee schedule, and a rate whose person is masked are left
 * alone, as the draft leaves them.
 *
 * A row still waiting for this party's decision on a counterparty change is left alone too. Editing
 * it starts from the counterparty's entry, so clearing its person would also take every other value
 * the counterparty asked for on that row — deciding the row for the user.
 */
export function withDepartedStaffingUnassigned(before: ProposalRedline, after: ProposalRedline): ProposalRedline {
  if (Masked.isMasked(before.vendorTeam) || Masked.isMasked(after.vendorTeam)) return after;
  if (Masked.isMasked(after.feeSchedule)) return after;

  const remaining = after.vendorTeam.currentEntries;
  const departed = before.vendorTeam.currentEntries.filter(
    (member) => !remaining.some((stayed) => stayed.isSamePersonAs(member)),
  );
  if (departed.length === 0) return after;

  let feeSchedule = after.feeSchedule;
  let changed = false;
  for (const rate of after.feeSchedule.redlines) {
    const standing = rate.currentEntry;
    if (!rate.isResolved || rate.isRemoved || !standing?.teamMember || standing.teamMemberIsMasked) continue;
    if (!departed.some((member) => member.isSamePersonAs(standing.teamMember))) continue;
    feeSchedule = feeSchedule.reviseEntryById(rate.field.id!, standing, withoutFeeScheduleTeamMember(standing));
    changed = true;
  }
  return changed ? after.updateFeeScheduleRedline(feeSchedule) : after;
}
